import { useCallback, useEffect, useImperativeHandle, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { setEditable } from '@lyearn/lygo-blocks';
import { EditorView } from '@lyearn/lygo-core';
import useAnalytics from '@/components/Analytics/hooks/useAnalytics';
import { useGenerativeTaskContext } from '@/components/LyearnAI/context/generativeTaskContext';
import { TransformTextInput } from '@/types/schema';

import { AIEditorActionMap } from '../consts';
import { useAIEditorContext } from '../context/AIEditorContext';
import { AIEditorActionTypes, AIEditorState } from '../types';
import { discardSuggestion, insertBelow, keepSuggestion } from '../utils/editorFunctions';
import { getAIEditorMenuItems } from '../utils/getEditorMenuItems';
import { getInputForTransformation } from '../utils/getInputForTransformation';

interface useAIEditorMenuItemsParams {
  editorView?: EditorView;
}

export default function useAIEditorMenu({ editorView }: useAIEditorMenuItemsParams) {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const [isPopoverVisible, { on: showPopover, off: hidePopover, toggle: togglePopover }] =
    useToggle(false);

  const { clearGenerativeTask, isLoading, isTyping, errorHandlerRef } = useGenerativeTaskContext();

  const {
    suggestedText,
    editorState,
    textUseCase,
    doTransformText,
    doTryAgain,
    topic,
    onTopicError,
  } = useAIEditorContext();

  const menuItems = useMemo(
    () => getAIEditorMenuItems({ editorState, t, textUseCase: textUseCase }),
    [editorState, t, textUseCase],
  );

  const onError = useCallback(() => {
    if (!editorView) return;

    hidePopover();
  }, [editorView, hidePopover]);

  useImperativeHandle(errorHandlerRef, () => onError, [onError]);

  const cleanupSuggestions = useCallback(() => {
    hidePopover();
    clearGenerativeTask();
  }, [clearGenerativeTask, hidePopover]);

  const onPopoverClose = useCallback(
    (editorView: EditorView) => {
      switch (editorState) {
        case AIEditorState.EmptyCompletion:
          keepSuggestion({
            editorView,
            suggestion: suggestedText,
            onDone: cleanupSuggestions,
          });
          break;
        case AIEditorState.DraftCompletion:
          insertBelow({
            editorView,
            suggestion: suggestedText,
            onDone: cleanupSuggestions,
          });
          break;
      }
    },
    [cleanupSuggestions, editorState, suggestedText],
  );

  // Scroll to bottom of editor, when suggestedText changes
  useEffect(() => {
    if (!editorView) return;

    const scrollEle = editorView.dom.closest('.ly-editor-core');
    if (!scrollEle) return;

    setTimeout(() => {
      scrollEle.scrollTo(0, scrollEle.scrollHeight + 300);
    }, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedText, isTyping]);

  useEffect(() => {
    // @ts-ignore
    if (!editorView || !editorView.docView) return;

    setEditable(editorView, !isTyping);
  }, [editorView, isTyping]);

  /**
   * what to do when user clicks away from popover
   * without choosing an action
   */
  const onPopoverOpenChange = useCallback(
    (nextValue: boolean) => {
      // open / close popover
      togglePopover(nextValue);

      // on close action
      if (nextValue === true) return;

      if (!editorView) return;

      // apply default action
      onPopoverClose(editorView);
    },
    [onPopoverClose, editorView, togglePopover],
  );

  /**
   * what to do when user activates AI on empty editor
   */
  const onEmptyMenuClick = useCallback(() => {
    if (!editorView) return;
    if (!topic) {
      onTopicError();
      return;
    }

    track('Description box: Ask Lyearn AI', { module: textUseCase });

    // default action
    const editorActionProps = AIEditorActionMap[AIEditorActionTypes.WriteWithLyearnAI].props;
    if (editorActionProps) {
      doTransformText({
        text: topic,
        ...editorActionProps,
      });
      showPopover();
    }
  }, [doTransformText, editorView, onTopicError, showPopover, textUseCase, topic, track]);

  const onAITransformations = useCallback(
    (editorActionProps: Pick<TransformTextInput, 'transformation' | 'tone'>) => {
      if (!editorView) return;

      doTransformText({
        ...editorActionProps,
        text: getInputForTransformation(suggestedText, editorView),
      });
    },
    [doTransformText, editorView, suggestedText],
  );

  const onAfterAISuggestions = useCallback(
    (editorActionFunction: typeof discardSuggestion) => {
      if (!editorView) return;

      editorActionFunction({
        editorView,
        suggestion: suggestedText,
        onDone: cleanupSuggestions,
      });
    },
    [cleanupSuggestions, editorView, suggestedText],
  );

  const onEditorCustomActions = useCallback(
    (actionType: AIEditorActionTypes) => {
      switch (actionType) {
        case AIEditorActionTypes.TryAgain:
          doTryAgain();
          break;
      }
    },
    [doTryAgain],
  );

  /**
   * what to do when user activates AI on editor with text
   */
  const onMenuItemAction = useCallback(
    (type: string) => {
      if (!editorView) return;

      const actionType = type as AIEditorActionTypes; // @aryan02420: can be removed after fixing the type
      const editorAction = AIEditorActionMap[actionType];

      track(`Description box: ${actionType}`, { module: textUseCase });
      // API actions
      if (editorAction.props) {
        onAITransformations(editorAction.props);
      }

      // Editor actions
      else if (editorAction.function) {
        onAfterAISuggestions(editorAction.function);
      }

      // Custom actions
      else if (editorAction.custom) {
        onEditorCustomActions(actionType);
      }
    },
    [
      editorView,
      onAITransformations,
      onAfterAISuggestions,
      onEditorCustomActions,
      textUseCase,
      track,
    ],
  );

  return {
    // state
    menuItems,
    isPopoverVisible: isPopoverVisible && !isLoading && !isTyping,

    // methods
    togglePopover,
    onPopoverOpenChange,
    onEmptyMenuClick,
    onMenuItemAction,
  };
}
